import { BusinessDate } from '../date/business-date';
import type { InspectionStatus } from '../planning/daily-inspection';
import type { PlanningChange } from '../planning/planning-change';
import { costOfQuarterHours, type HourlyRate } from '../workforce/labor-cost';
import { Money } from '../shared/money';
import type { ActivityId, ExecutionResourceId } from '../shared/ids';
import { ACTIVITY_STATUS_LABEL, type Activity, type ActivityStatus } from '../structure/activity';
import type { Deviation, HoursTotals, ResourceDayHours } from './hours-report';

/**
 * Os demais indicadores do painel — conferência, alteração, atividade e custo.
 *
 * Mesma regra do resto da pasta: funções puras sobre coleções já carregadas. O painel
 * inteiro é aritmética sobre o que as fases anteriores já gravaram; nada aqui inventa um
 * segundo cálculo do que já existe.
 */

// ── Conferência ─────────────────────────────────────────────────────────────────

export interface InspectionSummary {
  /** Dias-recurso com planejamento no período — o denominador da taxa. */
  readonly plannedCells: number;
  readonly confirmed: number;
  readonly adjusted: number;
  readonly absent: number;
  readonly pending: number;
  /** Conferidos ÷ planejados, em pontos percentuais. `undefined` quando não há o que conferir. */
  readonly ratePct?: number;
}

/**
 * Taxa de conferência do período.
 *
 * O denominador são os dias-recurso **com planejamento**: cobrar conferência de quem não
 * tinha nada planejado transformaria o indicador num retrato do calendário, não do
 * acompanhamento. Dia sem conferência aberta conta como pendente — é exatamente o que
 * ele é.
 */
export function summarizeInspections(rows: readonly ResourceDayHours[]): InspectionSummary {
  let plannedCells = 0;
  let confirmed = 0;
  let adjusted = 0;
  let absent = 0;
  let pending = 0;

  for (const row of rows) {
    const counted: InspectionStatus | 'NOT_INSPECTED' = row.status;
    if (row.plannedQuarterHours <= 0 && counted === 'NOT_INSPECTED') continue;
    plannedCells += 1;
    if (counted === 'CONFIRMED') confirmed += 1;
    else if (counted === 'ADJUSTED') adjusted += 1;
    else if (counted === 'ABSENT') absent += 1;
    else pending += 1;
  }

  const inspected = confirmed + adjusted + absent;
  return {
    plannedCells,
    confirmed,
    adjusted,
    absent,
    pending,
    ...(plannedCells === 0 ? {} : { ratePct: (inspected / plannedCells) * 100 }),
  };
}

// ── Alterações de planejamento ──────────────────────────────────────────────────

export interface ChangeDayCount {
  readonly date: string;
  readonly total: number;
  readonly withJustification: number;
}

export interface ChangeSummary {
  readonly total: number;
  readonly withJustification: number;
  readonly byDay: readonly ChangeDayCount[];
  /** Frentes mais alteradas no período, da mais alterada para a menos. */
  readonly topActivities: readonly { activityId: ActivityId; count: number }[];
}

/**
 * Quantas vezes o plano mudou, e quantas dessas trouxeram justificativa — a hora extra
 * (RN-292) ou o motivo que alguém quis deixar.
 *
 * Uma alteração que mexe em dois campos grava **duas linhas com a mesma sequência** e é
 * **uma** alteração (RN-071). Contar linhas dobraria o indicador exatamente nos casos que
 * mais interessam — trocar frente e horas de uma vez é o improviso típico do canteiro.
 */
export function summarizeChanges(changes: readonly PlanningChange[]): ChangeSummary {
  const seen = new Set<string>();
  const byDay = new Map<string, { total: number; withJustification: number }>();
  const byActivity = new Map<ActivityId, number>();
  let total = 0;
  let withJustification = 0;

  for (const change of changes) {
    const key = `${change.resourceId}|${change.date}|${String(change.sequence)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    total += 1;
    const justified = change.justification !== undefined;
    if (justified) withJustification += 1;

    const day = byDay.get(change.date) ?? { total: 0, withJustification: 0 };
    day.total += 1;
    if (justified) day.withJustification += 1;
    byDay.set(change.date, day);

    if (change.activityId !== undefined) {
      byActivity.set(change.activityId, (byActivity.get(change.activityId) ?? 0) + 1);
    }
  }

  return {
    total,
    withJustification,
    byDay: [...byDay.entries()]
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    topActivities: [...byActivity.entries()]
      .map(([activityId, count]) => ({ activityId, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10),
  };
}

// ── Atividades ──────────────────────────────────────────────────────────────────

export interface ActivitySummary {
  readonly total: number;
  readonly byStatus: readonly { status: ActivityStatus; label: string; count: number }[];
  /** `plannedEnd` vencido, sem `actualEnd` e sem estar encerrada (RN-124 pela data da obra). */
  readonly late: readonly ActivityId[];
}

/**
 * Situação das frentes da obra.
 *
 * "Atrasada" é comparada com o **hoje da obra** (RN-123), não com o instante do
 * navegador: uma obra em outro fuso não pode ver frentes atrasando meia noite antes.
 */
export function summarizeActivities(
  activities: readonly Activity[],
  today: BusinessDate,
): ActivitySummary {
  const counts = new Map<ActivityStatus, number>();
  const late: ActivityId[] = [];

  for (const activity of activities) {
    counts.set(activity.status, (counts.get(activity.status) ?? 0) + 1);

    if (activity.status === 'DONE' || activity.status === 'CANCELLED') continue;
    if (activity.plannedEnd === undefined || activity.actualEnd !== undefined) continue;
    if (activity.plannedEnd < today.toString()) late.push(activity.id);
  }

  const order: readonly ActivityStatus[] = ['PLANNED', 'IN_PROGRESS', 'DONE', 'CANCELLED'];
  return {
    total: activities.length,
    byStatus: order.map((status) => ({
      status,
      label: ACTIVITY_STATUS_LABEL[status],
      count: counts.get(status) ?? 0,
    })),
    late,
  };
}

// ── Custo ───────────────────────────────────────────────────────────────────────

export interface CostTotals {
  readonly planned: Money;
  readonly executed: Money;
  /** Recursos com preço cadastrado — os que entram nos totais acima. */
  readonly coveredResources: number;
  /**
   * Recursos sem preço no período.
   *
   * Enquanto for maior que zero o total é **parcial**, e a tela precisa dizer isso:
   * "custo indisponível" e "custa zero" são coisas diferentes (RN-055), e somar o
   * segundo no lugar do primeiro produz um custo de obra plausível e errado.
   */
  readonly resourcesWithoutRate: number;
  /** Quanto do total veio de serviço cobrado por produção, e não por hora. */
  readonly unitPricedPlanned: Money;
  readonly unitPricedExecuted: Money;
}

/**
 * Serviço terceirizado cobrado por **produção**.
 *
 * As horas dele não entram na conta: a obra contratou m² de alvenaria, e quantas pessoas
 * a empreiteira colocou para levantá-la é problema dela. Por isso a quantidade vem das
 * alocações — a meta do dia e a medição da conferência —, e não da malha de horas.
 */
export interface UnitPricedResource {
  readonly resourceId: ExecutionResourceId;
  readonly unitCost: Money;
  readonly plannedQuantity: number;
  readonly executedQuantity: number;
}

export interface AggregateCostInput {
  readonly rows: readonly ResourceDayHours[];
  /**
   * Custo/hora do recurso **naquele mês**.
   *
   * Por mês porque o divisor é o das horas úteis do mês (RN-059): dezembro, com feriados,
   * custa mais por hora que agosto, e um divisor médio esconderia isso justamente onde a
   * diferença importa.
   */
  readonly rateOf: (resourceId: ExecutionResourceId, monthKey: string) => HourlyRate | undefined;
  /** Serviços cobrados por produção — somados por quantidade, nunca por hora. */
  readonly unitPriced?: readonly UnitPricedResource[];
}

export function aggregateCost(input: AggregateCostInput): CostTotals {
  let planned = Money.ZERO;
  let executed = Money.ZERO;
  const covered = new Set<ExecutionResourceId>();
  const missing = new Set<ExecutionResourceId>();

  // Quem é cobrado por produção sai da conta de horas antes de ela começar: contá-lo nos
  // dois lugares dobraria o custo dele no total da obra.
  const byQuantity = new Map(
    (input.unitPriced ?? []).map((service) => [service.resourceId, service]),
  );

  for (const row of input.rows) {
    if (byQuantity.has(row.resourceId)) continue;
    if (row.plannedQuarterHours <= 0 && (row.executedQuarterHours ?? 0) <= 0) continue;

    const monthKey = BusinessDate.of(row.date).monthKey();
    const rate = input.rateOf(row.resourceId, monthKey);
    if (rate === undefined) {
      missing.add(row.resourceId);
      continue;
    }

    covered.add(row.resourceId);
    const plannedCost = costOfQuarterHours(rate, row.plannedQuarterHours);
    if (plannedCost !== undefined) planned = planned.plus(plannedCost);
    if (row.executedQuarterHours !== undefined) {
      const executedCost = costOfQuarterHours(rate, row.executedQuarterHours);
      if (executedCost !== undefined) executed = executed.plus(executedCost);
    }
  }

  let unitPlanned = Money.ZERO;
  let unitExecuted = Money.ZERO;
  for (const service of byQuantity.values()) {
    if (service.plannedQuantity <= 0 && service.executedQuantity <= 0) continue;
    covered.add(service.resourceId);
    unitPlanned = unitPlanned.plus(service.unitCost.times(service.plannedQuantity));
    unitExecuted = unitExecuted.plus(service.unitCost.times(service.executedQuantity));
  }

  return {
    planned: planned.plus(unitPlanned),
    executed: executed.plus(unitExecuted),
    coveredResources: covered.size,
    resourcesWithoutRate: missing.size,
    unitPricedPlanned: unitPlanned,
    unitPricedExecuted: unitExecuted,
  };
}

// ── Leituras derivadas dos totais ───────────────────────────────────────────────
//
// Percentuais que a tela mostraria de qualquer jeito. Ficam aqui, e não no componente,
// pela mesma razão que o saldo fica: uma conta feita em dois lugares é uma conta que um
// dia diverge. Todos devolvem `undefined` quando não há denominador — 0h disponíveis não
// são "0% de uso", são ausência de informação.

/** Planejado sobre o disponível. Passa de 100% na sobrecarga, e isso é informação. */
export function capacityUsePct(totals: HoursTotals): number | undefined {
  if (totals.availableQuarterHours <= 0) return undefined;
  return (totals.plannedQuarterHours / totals.availableQuarterHours) * 100;
}

/**
 * Realizado sobre o planejado **dos mesmos dias** — só os já conferidos.
 *
 * É a única comparação justa no meio do período: o planejado do mês inteiro contra o
 * realizado de doze dias diria que a obra anda devagar, quando ela só ainda não chegou
 * ao fim do mês.
 */
export function executionPct(totals: HoursTotals): number | undefined {
  if (totals.plannedInspectedQuarterHours <= 0) return undefined;
  return (totals.executedQuarterHours / totals.plannedInspectedQuarterHours) * 100;
}

export interface PeriodProgress {
  /** Dias corridos do período que já passaram, contando o hoje. */
  readonly elapsedDays: number;
  readonly totalDays: number;
  readonly pct: number;
}

/** Onde o hoje da obra cai dentro do período — travado nas pontas. */
export function periodProgress(from: string, to: string, today: string): PeriodProgress {
  const start = BusinessDate.of(from);
  const end = BusinessDate.of(to);
  const now = BusinessDate.of(today);
  const totalDays = Math.max(1, start.daysUntil(end) + 1);
  const elapsedDays = Math.min(totalDays, Math.max(0, start.daysUntil(now) + 1));
  return { elapsedDays, totalDays, pct: (elapsedDays / totalDays) * 100 };
}

/**
 * Os recortes que mais fugiram do plano, para cima ou para baixo.
 *
 * Quem não desviou não entra: uma lista de desvios com "0h" no meio é uma lista que
 * esconde os que importam entre os que não importam.
 */
export function largestDeviations<T extends { readonly deviation: Deviation }>(
  groups: readonly T[],
  limit: number,
): T[] {
  return groups
    .filter((group) => group.deviation.deltaQuarterHours !== 0)
    .sort(
      (a, b) => Math.abs(b.deviation.deltaQuarterHours) - Math.abs(a.deviation.deltaQuarterHours),
    )
    .slice(0, limit);
}

export interface Completion {
  readonly done: number;
  /** As frentes que ainda contam — cancelada não é frente por fazer. */
  readonly considered: number;
  readonly pct?: number;
}

export function completionOf(summary: ActivitySummary): Completion {
  const countOf = (status: ActivityStatus): number =>
    summary.byStatus.find((item) => item.status === status)?.count ?? 0;
  const done = countOf('DONE');
  const considered = summary.total - countOf('CANCELLED');
  return {
    done,
    considered,
    ...(considered === 0 ? {} : { pct: (done / considered) * 100 }),
  };
}
